import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useL } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useSite } from "@/lib/site";
import { api } from "@/lib/api";
import { Logo, LangToggle } from "@/components/site/Navbar";
import AdminOverview from "@/components/admin/Overview";
import AdminMembers from "@/components/admin/Members";
import AdminBookings from "@/components/admin/Bookings";
import { AdminBlocks, AdminKeycards } from "@/components/admin/Misc";
import AdminPhotos from "@/components/admin/Photos";
import { AdminActivity } from "@/components/admin/Onboarding";
import AdminKeycardHandovers, { AdminBell } from "@/components/admin/Keycard";
import MemberDialog from "@/components/admin/MemberDialog";
import { AdminPlans, AdminSettings } from "@/components/admin/Config";

export default function Admin() {
  const L = useL();
  const { logout } = useAuth();
  const { reload } = useSite();
  const [plans, setPlans] = useState([]);
  const [params, setParams] = useSearchParams();
  const deep = params.get("member");
  const loadPlans = useCallback(() => api.get("/admin/plans").then((r) => setPlans(r.data)), []);
  useEffect(() => { loadPlans(); }, [loadPlans]);
  const tabs = [
    ["analytics", L("Aperçu", "Overview"), <AdminOverview />],
    ["handover", L("Remise de carte", "Keycard pickup"), <AdminKeycardHandovers />],
    ["members", L("Membres", "Members"), <AdminMembers plans={plans} />],
    ["activity", L("Sessions & incidents", "Sessions & incidents"), <AdminActivity />],
    ["bookings", L("Réservations", "Reservations"), <AdminBookings />],
    ["maintenance", L("Maintenance", "Maintenance"), <AdminBlocks />],
    ["plans", L("Forfaits", "Plans"), <AdminPlans plans={plans} onSaved={() => { loadPlans(); reload(); }} />],
    ["keycards", L("Cartes", "Keycards"), <AdminKeycards />],
    ["gallery", L("Photos", "Photos"), <AdminPhotos onChange={reload} />],
    ["settings", L("Réglages", "Settings"), <AdminSettings onSaved={reload} />],
  ];
  return (
    <div className="min-h-screen bg-[#08080a]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
          <Link to="/" data-testid="admin-home-link"><Logo /></Link>
          <div className="flex items-center gap-3">
            <AdminBell />
            <LangToggle />
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-sm text-slate-200 hover:border-red-500 hover:text-white"><LogOut size={16} />{L("Se déconnecter", "Log Out")}</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-10">
        <p className="eyebrow">{L("Propriétaire", "Owner")}</p>
        <h1 className="mb-8 font-display text-5xl uppercase text-white">{L("Tableau de bord", "Dashboard")}</h1>
        <Tabs defaultValue={params.get("tab") || "handover"}>
          <TabsList className="mb-6 flex h-auto w-full flex-wrap justify-start gap-1 bg-white/5 p-1">
            {tabs.map(([k, label]) => <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="data-[state=active]:bg-red-600 data-[state=active]:text-white">{label}</TabsTrigger>)}
          </TabsList>
          {tabs.map(([k, , el]) => <TabsContent key={k} value={k}>{el}</TabsContent>)}
        </Tabs>
        {deep && <MemberDialog id={deep} plans={plans} onClose={() => { params.delete("member"); setParams(params); }} />}
      </main>
    </div>
  );
}
